import cytoscape from 'cytoscape';

function toElements(graphData) {
    const nodes = Array.isArray(graphData?.nodes) ? graphData.nodes : [];
    const edges = Array.isArray(graphData?.edges) ? graphData.edges : [];

    return [...nodes, ...edges];
}

function defaultStyle() {
    return [
        {
            selector: 'node',
            style: {
                'label': 'data(label)',
                'text-wrap': 'wrap',
                'text-max-width': 150,
                'font-size': 10,
                'font-weight': 600,
                'text-valign': 'center',
                'text-halign': 'center',
                'background-color': '#e2e8f0',
                'border-color': '#94a3b8',
                'border-width': 1,
                'color': '#0f172a',
                'padding': '8px',
            }
        },
        {
            selector: 'node[type = "source_group"]',
            style: {
                'shape': 'round-rectangle',
                'width': 150,
                'height': 54,
                'background-color': '#dbeafe',
                'border-color': '#2563eb',
                'border-width': 2,
            }
        },
        {
            selector: 'node[type = "destination"]',
            style: {
                'shape': 'round-rectangle',
                'width': 170,
                'height': 58,
                'background-color': '#dcfce7',
                'border-color': '#16a34a',
            }
        },
        {
            selector: 'node[status_key != "active_public"][type = "destination"]',
            style: {
                'background-color': '#fef3c7',
                'border-color': '#d97706',
            }
        },
        {
            selector: 'node[type = "external"]',
            style: {
                'shape': 'ellipse',
                'width': 82,
                'height': 82,
                'background-color': '#e0f2fe',
                'border-color': '#0284c7',
                'font-size': 9,
            }
        },
        {
            selector: 'node[type = "special"]',
            style: {
                'shape': 'ellipse',
                'width': 78,
                'height': 78,
                'background-color': '#f1f5f9',
                'border-color': '#64748b',
                'font-size': 9,
            }
        },
        {
            selector: 'node[type = "broken"]',
            style: {
                'shape': 'diamond',
                'width': 86,
                'height': 86,
                'background-color': '#ffe4e6',
                'border-color': '#e11d48',
                'border-width': 2,
                'font-size': 9,
            }
        },
        {
            selector: 'edge',
            style: {
                'curve-style': 'bezier',
                'target-arrow-shape': 'triangle',
                'arrow-scale': 0.8,
                'line-color': '#94a3b8',
                'target-arrow-color': '#94a3b8',
                'width': 1.4,
                'label': '',
                'font-size': 9,
                'text-rotation': 'autorotate',
                'text-margin-y': -8,
                'color': '#334155',
            }
        },
        {
            selector: 'edge[status_key = "broken_internal"]',
            style: {
                'line-color': '#e11d48',
                'target-arrow-color': '#e11d48',
                'width': 2,
            }
        },
        {
            selector: 'edge[status_key = "target_not_public"]',
            style: {
                'line-color': '#d97706',
                'target-arrow-color': '#d97706',
                'width': 2,
            }
        },
        {
            selector: 'edge[status_key = "external"]',
            style: {
                'line-style': 'dashed',
            }
        },
        {
            selector: 'edge:selected',
            style: {
                'label': 'data(label)',
                'line-color': '#0ea5e9',
                'target-arrow-color': '#0ea5e9',
                'width': 3,
            }
        },
        {
            selector: 'node:selected',
            style: {
                'border-color': '#0ea5e9',
                'border-width': 3,
            }
        },
        {
            selector: '.hidden-by-filter',
            style: {
                'display': 'none'
            }
        },
        {
            selector: '.dimmed',
            style: {
                'opacity': 0.18
            }
        },
        {
            selector: '.highlighted',
            style: {
                'opacity': 1
            }
        }
    ];
}

function defaultLayout(elementCount = 0) {
    if (elementCount > 700) {
        return {
            name: 'grid',
            animate: false,
            padding: 48,
            avoidOverlap: true,
            fit: true,
        };
    }

    const directedLayout = {
        name: 'breadthfirst',
        directed: true,
        circle: false,
        grid: false,
        animate: false,
        padding: 48,
        avoidOverlap: true,
        roots: 'node[type = "source_group"]',
    };

    if (elementCount > 220) {
        return {
            ...directedLayout,
            spacingFactor: 1.35,
        };
    }

    return {
        ...directedLayout,
        spacingFactor: 1.8,
    };
}

function resizeAndFit(cy, elements = undefined) {
    if (!cy || !cy.elements().length) {
        return;
    }

    cy.resize();
    cy.fit(elements, 34);
    cy.center();
}

function scheduleResizeAndFit(cy, elements = undefined) {
    resizeAndFit(cy, elements);

    if (typeof window === 'undefined') {
        return;
    }

    const runAfterFrame = typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame.bind(window)
        : (callback) => window.setTimeout(callback, 16);

    runAfterFrame(() => {
        resizeAndFit(cy, elements);

        window.setTimeout(() => {
            resizeAndFit(cy, elements);
        }, 80);
    });
}

function create(container, graphData, options = {}) {
    if (!container) {
        return null;
    }

    const elements = toElements(graphData);

    const cy = cytoscape({
        container,
        elements,
        style: defaultStyle(),
        layout: options.layout || defaultLayout(elements.length),
        hideEdgesOnViewport: elements.length > 700,
        textureOnViewport: elements.length > 700,
        wheelSensitivity: 0.22,
        minZoom: 0.16,
        maxZoom: 2.4,
    });

    cy.ready(() => {
        scheduleResizeAndFit(cy);
    });

    return cy;
}

function update(cy, graphData) {
    if (!cy) {
        return null;
    }

    const elements = toElements(graphData);

    cy.elements().remove();
    cy.add(elements);
    const layout = cy.layout(defaultLayout(elements.length));

    if (typeof layout.on === 'function') {
        layout.on('layoutstop', () => {
            scheduleResizeAndFit(cy);
        });
    }
    layout.run();
    scheduleResizeAndFit(cy);

    return cy;
}

function destroyIfExists(cy) {
    if (cy && typeof cy.destroy === 'function') {
        cy.destroy();
    }
}

function fitAll(cy) {
    if (cy) {
        resizeAndFit(cy);
    }
}

function zoomIn(cy) {
    if (!cy) {
        return;
    }

    cy.zoom({
        level: cy.zoom() * 1.2,
        renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
    });
}

function zoomOut(cy) {
    if (!cy) {
        return;
    }

    cy.zoom({
        level: cy.zoom() / 1.2,
        renderedPosition: { x: cy.width() / 2, y: cy.height() / 2 },
    });
}

function focusOnNode(cy, nodeId) {
    if (!cy || !nodeId) {
        return;
    }

    const node = cy.getElementById(nodeId);

    if (!node || !node.length) {
        return;
    }

    cy.elements().removeClass('highlighted dimmed');
    const neighborhood = node.closedNeighborhood();
    cy.elements().not(neighborhood).addClass('dimmed');
    neighborhood.addClass('highlighted');
    cy.animate({
        fit: {
            eles: neighborhood,
            padding: 80,
        },
        duration: 180,
    });
}

function clearFocus(cy) {
    if (!cy) {
        return;
    }

    cy.elements().removeClass('highlighted dimmed');
}

if (typeof window !== 'undefined') {
    window.PonoWebLinksMap = {
        create,
        update,
        destroyIfExists,
        fitAll,
        zoomIn,
        zoomOut,
        focusOnNode,
        clearFocus,
    };
}
